// frontend/lib/apiClient.ts
/**
 * API client for communicating with the FastAPI backend.
 */

const apiBaseUrl =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000/api/v1";
const API_BASE_URL = apiBaseUrl.replace(/\/$/, "");

interface ApiError extends Error {
  status?: number;
  body?: unknown;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }

    const error = new Error(
      (body as { detail?: string; message?: string })?.detail ||
        (body as { message?: string })?.message ||
        `Request failed with ${res.status}`
    ) as ApiError;
    error.status = res.status;
    error.body = body;
    throw error;
  }

  if (res.status === 204) {
    return null as T;
  }

  return res.json();
}

/**
 * Get the auth token from localStorage (or wherever you store it).
 * Override this for your auth implementation.
 */
function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

/**
 * Get default headers including auth token if available.
 */
function getHeaders(init?: RequestInit, tokenOverride?: string | null): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) || {}),
  };

  const token = tokenOverride ?? getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      // Refresh token is sent via httpOnly cookie (credentials: "include")
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!res.ok) {
        // Clear legacy tokens on failure
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        return null;
      }

      const data = await res.json();
      if (data?.access_token) {
        localStorage.setItem("access_token", data.access_token);
        return data.access_token as string;
      }

      return null;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

async function request<T>(
  path: string,
  init: RequestInit,
  retryOnUnauthorized = true
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: getHeaders(init),
    credentials: "include",
  });

  if (res.status === 401 && retryOnUnauthorized && !path.startsWith("/auth/refresh")) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryRes = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers: getHeaders(init, newToken),
        credentials: "include",
      });
      return handleResponse<T>(retryRes);
    }
  }

  return handleResponse<T>(res);
}

/**
 * Perform a GET request to the API.
 */
export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, {
    ...init,
    method: "GET",
  });
}

/**
 * Perform a POST request to the API.
 */
export async function apiPost<T>(
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  return request<T>(path, {
    ...init,
    method: "POST",
    headers: getHeaders(init),
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Perform a PUT request to the API.
 */
export async function apiPut<T>(
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  return request<T>(path, {
    ...init,
    method: "PUT",
    headers: getHeaders(init),
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Perform a DELETE request to the API.
 */
export async function apiDelete<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  return request<T>(path, {
    ...init,
    method: "DELETE",
  });
}
